/**
 * UI labels — display-only mappings for the management console.
 *
 * These are NOT translations of any backend field. They are the
 * Chinese strings the operator sees in the UI. Backend enum values
 * stay in English on the wire; this module maps them at the edge.
 *
 * Add new mappings here rather than scattering Chinese strings across
 * page components. If a value isn't listed, fall back to the enum
 * itself (or '' for empty) — never invent.
 */

/** Section type — matches EditorSectionType in lib/types.ts */
export const SECTION_TYPE_LABELS: Record<string, string> = {
  hero: '首屏 Banner',
  features: '核心优势',
  about: '关于我们',
  products: '产品 / 服务',
  cta: '行动引导',
  faq: '常见问题',
};

/** Site hosting mode — matches Site.hosting_mode */
export const HOSTING_MODE_LABELS: Record<string, string> = {
  CUSTOMER_SERVER: '客户服务器',
  MANAGED_HOSTING: '梧炅托管',
  EXISTING_WORDPRESS: '已有 WordPress 网站',
  NO_DEPLOY: '暂不部署',
};

/** Site status — matches Site.status */
export const SITE_STATUS_LABELS: Record<string, string> = {
  DRAFT: '草稿',
  SERVER_PENDING: '等待服务器',
  DEPLOYING: '部署中',
  WP_INSTALLED: '已安装 WordPress',
  TEMPLATE_APPLIED: '已应用模板',
  TEMPLATE_APPLYING: '应用模板中',
  CONTENT_PENDING: '等待内容',
  READY: '就绪',
  PUBLISHED: '已发布',
  FAILED: '失败',
  SUSPENDED: '已暂停',
};

/** Server status — matches Server.status */
export const SERVER_STATUS_LABELS: Record<string, string> = {
  UNKNOWN: '未知',
  ONLINE: '在线',
  OFFLINE: '离线',
  ERROR: '异常',
};

/** Server ownership — matches Server.ownership */
export const SERVER_OWNERSHIP_LABELS: Record<string, string> = {
  SELF: '自有（我们的资源池）',
  CUSTOMER: '客户自有',
};

/** Server management mode — matches Server.management_mode */
export const SERVER_MGMT_LABELS: Record<string, string> = {
  MANAGED: '代管',
  EXTERNAL: '外部',
};

/** Server auth type — matches Server.auth_type */
export const SERVER_AUTH_LABELS: Record<string, string> = {
  PASSWORD: '密码',
  SSH_KEY: 'SSH 私钥',
};

/** Customer status — matches Customer.status */
export const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  ACTIVE: '正常',
  INACTIVE: '已停用',
};

/** Deployment job status — matches DeploymentJob.status */
export const JOB_STATUS_LABELS: Record<string, string> = {
  QUEUED: '排队中',
  RUNNING: '执行中',
  SUCCESS: '成功',
  FAILED: '失败',
  CANCELLED: '已取消',
};

/** Deployment step status — matches DeploymentStep.status */
export const STEP_STATUS_LABELS: Record<string, string> = {
  PENDING: '等待中',
  RUNNING: '执行中',
  SUCCESS: '成功',
  FAILED: '失败',
  SKIPPED: '已跳过',
};

/** Site plan / AI build protection status */
export const PROTECTION_LABELS: Record<string, string> = {
  protected: '已保护',
  pending: '等待中',
  unprotected: '未保护',
};

/** Domain DNS status */
export const DNS_STATUS_LABELS: Record<string, string> = {
  PENDING: '等待检测',
  OK: '正常',
  MISMATCH: '不匹配',
  NXDOMAIN: '未解析',
  UNKNOWN: '未知',
};

/** Media asset source */
export const MEDIA_SOURCE_LABELS: Record<string, string> = {
  IMAGE_SERVICE: 'AI 生成',
  WORDPRESS: 'WordPress',
  UPLOAD: '本地上传',
};

/** Media asset status */
export const MEDIA_STATUS_LABELS: Record<string, string> = {
  PENDING: '等待中',
  READY: '已就绪',
  PUBLISHED: '已发布',
  FAILED: '失败',
  EXPIRED: '已过期',
};

/** Page role — matches EditorPage.role */
export const PAGE_ROLE_LABELS: Record<string, string> = {
  home: '首页',
  landing: '落地页',
  about: '关于',
  product: '产品',
  contact: '联系',
  legal: '法律',
  other: '其他',
};

/** Nav item target */
export const NAV_TARGET_LABELS: Record<string, string> = {
  _self: '当前窗口',
  _blank: '新窗口',
};

/** Nav link kind */
export const NAV_LINK_KIND_LABELS: Record<string, string> = {
  page: '内部页面',
  url: '外部链接',
};

/** Look up a label, fall back to the raw key if unknown. */
export function labelOf(map: Record<string, string>, key: string | null | undefined, fallback?: string): string {
  if (!key) return fallback ?? '';
  return map[key] ?? fallback ?? key;
}
